import { Node, Edge } from 'modules/workflows/types';
import { WorkflowState } from 'modules/workflows/ducks/ducks.types';
import {
  CloneEdgesAction,
  JourneyVisitorMetricsResponseAction,
  WorkflowViewAction,
} from '../../actions';
import { colors, fonts } from 'ui/theme';
import { WorkflowView } from 'modules/workflows/constants/workflow';
import { DELAY_NODE_TYPES, MODIFYFLOW_NODE_TYPES } from 'ui/workflows';

// maps original to cloned node ids
const getOriginalToClonedNodeMap = (nodes: Record<string, Node> = {}): Record<string, Node> =>
  Object.values(nodes).reduce((cloned, currNode) => {
    const originalId = currNode?.clonedFromId;
    if (originalId) {
      return { ...cloned, [originalId]: currNode.id };
    }
    return cloned;
  }, {});

export const cloneEdges = (state: Edge[], action: CloneEdgesAction, wfState: WorkflowState) => {
  const { edges = [] } = action.payload;
  const { nodes } = wfState;

  const nodeMap = getOriginalToClonedNodeMap(nodes);

  const newEdges = edges.map((edge) => ({
    ...edge,
    source: nodeMap?.[edge.source as string],
    target: nodeMap?.[edge.target as string],
  }));

  return [...state, ...newEdges];
};

// Utility function to construct labelCfg style
const createLabelCfg = () => ({
  style: {
    stroke: colors.grey5, // White stroke for the label
    lineWidth: 15, // The line width of the stroke
    fill: colors.textSecondary,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 16,
    fontFamily: fonts.sansSerif,
    padding: [5, 10], // Padding around the text [top, right, bottom, left]
    radius: 5, // Border radius for rounded corners
  },
});

export const getPercentage = (num?: number) => {
  if (num === undefined) return undefined;
  if (num <= 0) return 0;
  if (num === 1) return 100;

  const rounded = Math.trunc(num * 100);
  if (rounded < 1 || rounded >= 99) {
    if (rounded < 1) return 1;
    if (rounded >= 99) return 99;
  }
  return Math.round(num * 100);
};

export const getEdgePercentageFromEdge = (
  edge: Edge,
  tileVisitorMetric?: {
    edgeVisitorPercentages: Array<{
      srcNodeId: number;
      destNodeId: number;
      srcOutputIdx: number;
      percentageOfVisitors: number;
    }>;
  },
) => {
  if (!tileVisitorMetric) return undefined;

  const edgeVisitorPercentage = tileVisitorMetric?.edgeVisitorPercentages?.find(
    ({ srcNodeId, destNodeId, srcOutputIdx }) =>
      edge.srcWorkflowNodeId === srcNodeId &&
      edge.destWorkflowNodeId === destNodeId &&
      srcOutputIdx === edge.srcWorkflowNodeOutputIndex,
  );
  return getPercentage(edgeVisitorPercentage?.percentageOfVisitors);
};

const edgeSupportedEdgePercentageNodeTypes = [
  MODIFYFLOW_NODE_TYPES.AB_SPLIT,
  MODIFYFLOW_NODE_TYPES.ATTRIBUTE_SPLIT,
  MODIFYFLOW_NODE_TYPES.FIELDS_MATCH,
  DELAY_NODE_TYPES.HOLD,
];

export const updatePercentageOnEdges = (
  edges: Edge[],
  action: JourneyVisitorMetricsResponseAction,
  wfState: WorkflowState,
) => {
  const tileVisitorMetrics = action.payload?.journeyVisitorMetrics?.tileVisitorMetrics ?? [];
  return edges.map((edge) => {
    if (!edge?.srcWorkflowNodeId) {
      return { ...edge, label: undefined };
    }
    const node = wfState?.nodes?.[edge.srcWorkflowNodeId];
    const nodeType = node?.type;
    if (edgeSupportedEdgePercentageNodeTypes.includes(nodeType)) {
      const tileMetric = tileVisitorMetrics?.find(
        (tileVisitorMetric) => tileVisitorMetric?.nodeId === edge?.srcWorkflowNodeId,
      );
      const percentage = getEdgePercentageFromEdge(edge, tileMetric);

      return {
        ...edge,
        label: percentage ? `${percentage}%` : undefined,
        labelCfg: createLabelCfg(),
      };
    }
    return { ...edge, label: undefined };
  });
};

export const updateEdgesOnWorkflowViewChange = (
  edges: Edge[],
  action: WorkflowViewAction,
  wfState: WorkflowState,
) => {
  const { workflowView } = action.payload;
  if (workflowView === WorkflowView.InProgress) {
    return edges?.map((edge) => ({
      ...edge,
      label: undefined,
    }));
  } else {
    const nodes = wfState?.nodes;
    return edges?.map((edge) => {
      const { srcWorkflowNodeId } = edge;

      if (!srcWorkflowNodeId) {
        return { ...edge, label: undefined };
      }

      const node = nodes?.[srcWorkflowNodeId];
      const nodeType = node?.type;
      if (edgeSupportedEdgePercentageNodeTypes.includes(nodeType)) {
        const edgeVisitorPercentages = node?.edgeVisitorPercentages;
        const percentage = getEdgePercentageFromEdge(edge, { edgeVisitorPercentages });

        return {
          ...edge,
          label: percentage ? `${percentage}%` : undefined,
          labelCfg: createLabelCfg(), // Reuse the labelCfg utility function
        };
      } else {
        return {
          ...edge,
          label: undefined,
        };
      }
    });
  }
};
